/**
 * @module bridges
 * @package @geenius/adapters/solidjs-solidui
 * @description SolidUI-flavoured bridge components for adapter loading,
 * error, and toast states. The core adapter provider and primitives are
 * re-exported from the SolidJS reference package; this module only adds the
 * library-variant UI affordances required by the adapter contract.
 */

import type { LoggerAdapter, LogMeta } from "@geenius/adapters";
import {
  createEffect,
  For,
  type JSX,
  onCleanup,
  Show,
  ErrorBoundary as SolidErrorBoundary,
} from "solid-js";

type BridgeTone = "info" | "success" | "warning" | "error";

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

/**
 * Props accepted by the SolidUI loading bridge.
 *
 * @property loading Whether the bridge should render its loading indicator.
 * @property label Accessible loading label and visible title.
 * @property description Optional supporting text for longer async operations.
 * @property fullScreen Whether the indicator should fill the viewport.
 * @property class Optional class merged into the bridge wrapper.
 * @property children Content rendered when `loading` is false.
 */
export interface LoadingBridgeProps {
  loading?: boolean;
  label?: string;
  description?: string;
  fullScreen?: boolean;
  class?: string;
  children?: JSX.Element;
}

/**
 * Renders a SolidUI-style loading surface for async adapter work.
 *
 * @param props Loading bridge props.
 * @returns A token-friendly loading indicator or the supplied children.
 */
export function LoadingBridge(props: LoadingBridgeProps): JSX.Element {
  const label = () => props.label ?? "Loading adapters";
  const content = (
    <div
      aria-busy="true"
      aria-label={label()}
      class={cx(
        "flex flex-col items-center justify-center gap-3 rounded-md border border-border bg-card p-6 text-card-foreground shadow-sm",
        props.fullScreen && "min-h-screen rounded-none border-0",
        props.class,
      )}
      role="status"
    >
      <span
        aria-hidden="true"
        class="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"
      />
      <div class="space-y-1 text-center">
        <p class="text-sm font-semibold">{label()}</p>
        <Show when={props.description}>
          <p class="max-w-sm text-sm text-muted-foreground">
            {props.description}
          </p>
        </Show>
      </div>
    </div>
  );

  return (
    <Show when={props.loading ?? true} fallback={props.children}>
      {content}
    </Show>
  );
}

interface NormalizedError {
  code?: string;
  domain?: string;
  message: string;
  name?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === "string" ? value : undefined;
}

function normalizeError(error: unknown): NormalizedError {
  if (error instanceof Error) {
    const record = error as Error & Record<string, unknown>;
    return {
      code: readString(record, "adapterCode") ?? readString(record, "code"),
      domain: readString(record, "domain"),
      message: error.message,
      name: error.name,
    };
  }

  if (isRecord(error)) {
    return {
      code: readString(error, "adapterCode") ?? readString(error, "code"),
      domain: readString(error, "domain"),
      message: readString(error, "message") ?? "The adapter request failed.",
      name: readString(error, "name"),
    };
  }

  if (typeof error === "string") {
    return { message: error };
  }

  return { message: "The adapter request failed." };
}

/**
 * Props accepted by the SolidUI error boundary bridge.
 *
 * @property error Explicit async adapter error to render.
 * @property title Heading shown above the error details.
 * @property class Optional class merged into the error surface.
 * @property resetLabel Label for the reset button.
 * @property onError Callback invoked when a descendant render error is caught.
 * @property onReset Callback invoked by the reset button.
 * @property children Content protected by the SolidJS error boundary.
 */
export interface ErrorBoundaryBridgeProps {
  error?: unknown;
  title?: string;
  class?: string;
  resetLabel?: string;
  onError?: (error: unknown) => void;
  onReset?: () => void;
  children?: JSX.Element;
}

function ErrorPanel(props: {
  class?: string;
  error: unknown;
  onReset?: () => void;
  resetLabel?: string;
  title?: string;
}): JSX.Element {
  const detail = () => normalizeError(props.error);

  return (
    <section
      class={cx(
        "rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive shadow-sm",
        props.class,
      )}
      role="alert"
    >
      <div class="flex items-start gap-3">
        <span
          aria-hidden="true"
          class="mt-1 flex h-5 w-5 items-center justify-center rounded-full border border-current text-xs font-bold"
        >
          !
        </span>
        <div class="min-w-0 flex-1 space-y-1">
          <h2 class="text-sm font-semibold">
            {props.title ?? "Adapter error"}
          </h2>
          <p class="text-sm text-destructive/90">{detail().message}</p>
          <Show when={detail().code || detail().domain}>
            <p class="text-xs text-destructive/75">
              {[detail().domain, detail().code].filter(Boolean).join(" / ")}
            </p>
          </Show>
        </div>
        <Show when={props.onReset}>
          <button
            class="rounded-md border border-current px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-destructive/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/30"
            onClick={props.onReset}
            type="button"
          >
            {props.resetLabel ?? "Retry"}
          </button>
        </Show>
      </div>
    </section>
  );
}

/**
 * Wraps SolidJS's native error boundary with an adapter-specific SolidUI
 * fallback. Passing an explicit `error` renders the same fallback for async
 * adapter failures that happen outside render.
 *
 * @param props Error bridge props.
 * @returns Protected children or an adapter error panel.
 */
export function ErrorBoundaryBridge(
  props: ErrorBoundaryBridgeProps,
): JSX.Element {
  return (
    <Show
      when={props.error !== undefined}
      fallback={
        <SolidErrorBoundary
          fallback={(error, reset) => {
            props.onError?.(error);
            return (
              <ErrorPanel
                class={props.class}
                error={error}
                onReset={() => {
                  props.onReset?.();
                  reset();
                }}
                resetLabel={props.resetLabel}
                title={props.title}
              />
            );
          }}
        >
          {props.children}
        </SolidErrorBoundary>
      }
    >
      <ErrorPanel
        class={props.class}
        error={props.error}
        onReset={props.onReset}
        resetLabel={props.resetLabel}
        title={props.title}
      />
    </Show>
  );
}

/**
 * A single toast notification rendered by the SolidUI toast bridge.
 */
export interface ToastBridgeMessage {
  id: string;
  title: string;
  description?: string;
  tone?: BridgeTone;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  meta?: LogMeta;
}

export type ToastBridgePlacement =
  | "bottom-left"
  | "bottom-right"
  | "top-left"
  | "top-right";

/**
 * Props accepted by the SolidUI toast bridge.
 *
 * @property toasts Toast messages to render.
 * @property placement Fixed viewport placement for the toast stack.
 * @property class Optional class merged into the toast stack.
 * @property logger Optional logger adapter that receives one event per toast id.
 * @property onDismiss Callback fired when a toast is dismissed or expires.
 */
export interface ToastBridgeProps {
  toasts: readonly ToastBridgeMessage[];
  placement?: ToastBridgePlacement;
  class?: string;
  logger?: LoggerAdapter;
  onDismiss?: (id: string) => void;
}

const toastToneClasses: Record<BridgeTone, string> = {
  error:
    "border-destructive/30 bg-destructive/10 text-destructive shadow-destructive/10",
  info: "border-border bg-card text-card-foreground shadow-sm",
  success: "border-primary/30 bg-primary/10 text-primary shadow-primary/10",
  warning: "border-accent/30 bg-accent/10 text-accent shadow-accent/10",
};

const toastToneLabel: Record<BridgeTone, string> = {
  error: "Error",
  info: "Info",
  success: "Success",
  warning: "Warning",
};

const placementClasses: Record<ToastBridgePlacement, string> = {
  "bottom-left": "bottom-4 left-4",
  "bottom-right": "bottom-4 right-4",
  "top-left": "left-4 top-4",
  "top-right": "right-4 top-4",
};

function logToast(logger: LoggerAdapter, toast: ToastBridgeMessage): void {
  const tone = toast.tone ?? "info";
  const meta: LogMeta = {
    bridge: "solidjs-solidui",
    toastId: toast.id,
    tone,
    ...toast.meta,
  };

  if (tone === "error") {
    void logger.error(toast.title, meta);
    return;
  }

  if (tone === "warning") {
    void logger.warn(toast.title, meta);
    return;
  }

  void logger.info(toast.title, meta);
}

/**
 * Renders a SolidUI-style toast stack and optionally mirrors toast events to
 * the configured logger adapter.
 *
 * @param props Toast bridge props.
 * @returns A fixed toast region.
 */
export function ToastBridge(props: ToastBridgeProps): JSX.Element {
  const loggedToastIds = new Set<string>();
  const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

  createEffect(() => {
    const activeIds = new Set(props.toasts.map((toast) => toast.id));

    for (const [toastId, timer] of dismissTimers) {
      if (!activeIds.has(toastId)) {
        clearTimeout(timer);
        dismissTimers.delete(toastId);
      }
    }

    for (const toast of props.toasts) {
      if (props.logger && !loggedToastIds.has(toast.id)) {
        loggedToastIds.add(toast.id);
        logToast(props.logger, toast);
      }

      if (
        toast.duration !== undefined &&
        toast.duration > 0 &&
        props.onDismiss &&
        !dismissTimers.has(toast.id)
      ) {
        dismissTimers.set(
          toast.id,
          setTimeout(() => {
            props.onDismiss?.(toast.id);
            dismissTimers.delete(toast.id);
          }, toast.duration),
        );
      }
    }
  });

  onCleanup(() => {
    for (const timer of dismissTimers.values()) {
      clearTimeout(timer);
    }
    dismissTimers.clear();
  });

  return (
    <Show when={props.toasts.length > 0}>
      <div
        aria-live="polite"
        class={cx(
          "fixed z-50 flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2",
          placementClasses[props.placement ?? "top-right"],
          props.class,
        )}
        role="status"
      >
        <For each={props.toasts}>
          {(toast) => {
            const tone = () => toast.tone ?? "info";
            return (
              <article
                class={cx(
                  "rounded-md border p-4 shadow-sm",
                  toastToneClasses[tone()],
                )}
                data-toast-id={toast.id}
              >
                <div class="flex items-start gap-3">
                  <span class="text-xs font-bold uppercase tracking-normal">
                    {toastToneLabel[tone()]}
                  </span>
                  <div class="min-w-0 flex-1 space-y-1">
                    <p class="text-sm font-semibold">{toast.title}</p>
                    <Show when={toast.description}>
                      <p class="text-sm opacity-85">{toast.description}</p>
                    </Show>
                  </div>
                  <Show when={toast.action}>
                    {(action) => (
                      <button
                        class="rounded-md border border-current px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/30"
                        onClick={action().onClick}
                        type="button"
                      >
                        {action().label}
                      </button>
                    )}
                  </Show>
                  <Show when={props.onDismiss}>
                    <button
                      aria-label={`Dismiss ${toast.title}`}
                      class="rounded-md p-1 text-xs font-semibold opacity-75 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/30"
                      onClick={() => props.onDismiss?.(toast.id)}
                      type="button"
                    >
                      x
                    </button>
                  </Show>
                </div>
              </article>
            );
          }}
        </For>
      </div>
    </Show>
  );
}
